import { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, Image } from 'lucide-react';
import SEO from '../components/SEO';

export default function Gallery() {
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [activeFilter, setActiveFilter] = useState('alle'); // 'alle', 'forretter', 'hovedretter'

  const orderedCategories = [
    { slug: 'forretter', label: 'Forretter' },
    { slug: 'hovedretter', label: 'Hovedretter' }
  ];

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://api.dev.auxo.dk/public/content/gallery_items');
        if (!response.ok) {
          throw new Error('Kunne ikke hente galleriet.');
        }
        const data = await response.json();
        setGalleryItems(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, []);

  // Filter categories based on active filter
  const filteredCategories = activeFilter === 'alle'
    ? orderedCategories
    : orderedCategories.filter(cat => cat.slug === activeFilter);

  // Build the flattened list of items in the exact order they are displayed on the page
  const displayedItems = [];
  filteredCategories.forEach(cat => {
    const itemsInCat = galleryItems.filter(item => item.category_name === cat.slug);
    displayedItems.push(...itemsInCat);
  });

  // Lightbox navigation
  const openLightbox = (index) => {
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
  };

  const nextImage = (e) => {
    e.stopPropagation();
    if (displayedItems.length === 0) return;
    setLightboxIndex((prevIndex) => (prevIndex + 1) % displayedItems.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (displayedItems.length === 0) return;
    setLightboxIndex((prevIndex) => (prevIndex - 1 + displayedItems.length) % displayedItems.length);
  };

  // Handle keyboard navigation in lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage(e);
      if (e.key === 'ArrowLeft') prevImage(e);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxIndex, displayedItems]);

  return (
    <>
      <SEO
        title="Galleri | Autentisk thai mad i Skibby | Bestil take-away eller en foodtruck"
        description="Se billeder af vores lækre thailandske retter, herunder forretter og hovedretter tilberedt af friske råvarer."
      />

      {/* Header Banner */}
      <section className="relative pt-56 pb-16 bg-brand-dark text-white text-left">
        <div className="absolute inset-0 z-0 opacity-20">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/6f940026-1e92-4aa1-ae41-4b36c3c69c15/thai-foodtruck-premium.jpg"
            alt="Thai Food Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-brand-dark via-brand-dark/90 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
            Vores Galleri
          </h1>
          <p className="text-white/90 font-display text-lg sm:text-xl mt-2 font-semibold">
            Oplev stemningen og de lækre retter
          </p>
        </div>
      </section>

      {/* Gallery Navigation Tabs */}
      <section className="bg-white border-b border-gray-100 sticky top-[72px] md:top-[88px] z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 flex justify-start gap-2 sm:gap-4 py-4">
          <button
            onClick={() => setActiveFilter('alle')}
            className={`px-6 py-2.5 rounded-full text-sm font-semibold tracking-wide transition-all cursor-pointer border ${
              activeFilter === 'alle'
                ? 'bg-black text-white border-black shadow-sm'
                : 'bg-transparent text-black border-black hover:bg-black hover:text-white'
            }`}
          >
            Alle billeder
          </button>
          <button
            onClick={() => setActiveFilter('forretter')}
            className={`px-6 py-2.5 rounded-full text-sm font-semibold tracking-wide transition-all cursor-pointer border ${
              activeFilter === 'forretter'
                ? 'bg-black text-white border-black shadow-sm'
                : 'bg-transparent text-black border-black hover:bg-black hover:text-white'
            }`}
          >
            Forretter
          </button>
          <button
            onClick={() => setActiveFilter('hovedretter')}
            className={`px-6 py-2.5 rounded-full text-sm font-semibold tracking-wide transition-all cursor-pointer border ${
              activeFilter === 'hovedretter'
                ? 'bg-black text-white border-black shadow-sm'
                : 'bg-transparent text-black border-black hover:bg-black hover:text-white'
            }`}
          >
            Hovedretter
          </button>
        </div>
      </section>

      {/* Gallery Content */}
      <section className="py-16 bg-white min-h-[500px]">
        <div className="max-w-7xl mx-auto px-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-gray-500 font-medium">Henter billeder...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-100 text-red-700 p-6 rounded-2xl text-center max-w-lg mx-auto">
              <p className="font-semibold">{error}</p>
            </div>
          ) : displayedItems.length === 0 ? (
            <div className="text-center py-20 text-gray-500">
              Der er i øjeblikket ingen billeder i denne kategori.
            </div>
          ) : (
            <div className="flex flex-col gap-16">
              {filteredCategories.map((cat) => {
                const itemsInCat = galleryItems.filter(item => item.category_name === cat.slug);
                if (itemsInCat.length === 0) return null;

                return (
                  <div key={cat.slug} className="scroll-mt-24">
                    <div className="border-b border-gray-200 pb-4 mb-8">
                      <h2 className="font-display text-2xl sm:text-3xl font-bold text-brand-dark uppercase tracking-wider">
                        {cat.label}
                      </h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                      {itemsInCat.map((item) => {
                        // Find the global index of this item in the displayedItems array for the lightbox
                        const globalIndex = displayedItems.findIndex(di => di.id === item.id);
                        return (
                          <div
                            key={item.id}
                            onClick={() => openLightbox(globalIndex)}
                            className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 p-3 flex flex-col gap-3 group cursor-pointer hover:shadow-md transition-all hover:-translate-y-0.5"
                          >
                            <div className="overflow-hidden rounded-xl h-64 relative">
                              <img
                                src={item.image[0]}
                                alt={item.title || 'Galleri billede'}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                              />
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white">
                                  <Image className="w-6 h-6" />
                                </div>
                              </div>
                            </div>
                            <div className="px-2 pb-2">
                              <h3 className="font-display font-bold text-brand-dark text-base">
                                {item.title || 'Galleri billede'}
                              </h3>
                              {item.description && (
                                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                                  {item.description}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxIndex !== null && displayedItems[lightboxIndex] && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 sm:p-8"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white/70 hover:text-white p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-50 cursor-pointer"
            aria-label="Luk visning"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Navigation Buttons */}
          <button
            onClick={prevImage}
            className="absolute left-6 text-white/70 hover:text-white p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-50 cursor-pointer"
            aria-label="Forrige billede"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-6 text-white/70 hover:text-white p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-50 cursor-pointer"
            aria-label="Næste billede"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Main Content Area */}
          <div
            className="max-w-5xl max-h-[85vh] flex flex-col items-center justify-center gap-4"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={displayedItems[lightboxIndex].image[0]}
              alt={displayedItems[lightboxIndex].title || 'Galleri billede'}
              className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-2xl"
            />
            <div className="text-center text-white max-w-2xl px-4">
              <h3 className="font-display font-bold text-xl sm:text-2xl">
                {displayedItems[lightboxIndex].title || 'Galleri billede'}
              </h3>
              {displayedItems[lightboxIndex].description && (
                <p className="text-sm text-gray-400 mt-2 leading-relaxed">
                  {displayedItems[lightboxIndex].description}
                </p>
              )}
              <p className="text-xs text-brand-secondary font-semibold uppercase tracking-wider mt-3">
                Kategori: {orderedCategories.find(c => c.slug === displayedItems[lightboxIndex].category_name)?.label || displayedItems[lightboxIndex].category_name}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
